import React, { useState } from 'react';
import { Plus, Trash2, Snowflake } from 'lucide-react';
import { saldoProjetado, emDoses } from '../../../data/semen';
import { enderecoDoBotijao } from '../../../data/estoque';
import { MARK } from '../../../constants';
import SeletorDePalhetas from './SeletorDePalhetas';

/**
 * Palhetas descongeladas numa sessão de FIV.
 *
 * É AQUI que o estoque baixa — e só aqui. A gota, que fica na linha da
 * doadora, diz de qual palheta aquela doadora saiu e não debita nada. Quem
 * mistura as duas ou passa a dever palheta ao botijão, ou nunca consegue dizer
 * quantas ainda tem.
 *
 * O saldo mostrado é o PROJETADO: `saldo_doses` da view já desconta o que esta
 * sessão gravou da última vez, então reabrir uma sessão sem mexer em nada tem
 * de mostrar o mesmo número de antes, não o número descontado duas vezes.
 *
 * A partida é escolhida no SeletorDePalhetas, não num <select> por linha: com
 * o botijão inteiro numa lista de texto corrido ninguém achava a palheta. Aqui
 * fica só o que já foi escolhido; trocar a partida é tirar e escolher de novo.
 */
const SemenDaSessao = ({
  batches = [],
  semen = [],
  dosesGravadas = {},
  clientId = null,
  clientName = '',
  onChange,
  disabled = false,
}) => {
  const [escolhendo, setEscolhendo] = useState(false);
  const porId = Object.fromEntries(batches.map(b => [b.semen_batch_id, b]));
  const jaEscolhidas = semen.map(s => s.semen_batch_id).filter(Boolean);

  const alterar = (i, campo, valor) =>
    onChange(semen.map((s, idx) => (idx === i ? { ...s, [campo]: valor } : s)));

  const remover = (i) => onChange(semen.filter((_, idx) => idx !== i));

  /**
   * As escolhidas no seletor entram no fim. A linha sem partida sai: é a que o
   * "+ palheta" da doadora abre quando não acha o touro no estoque, e o botão
   * dela é justamente o que trouxe a pessoa até o seletor.
   */
  const adicionar = (novas) =>
    onChange([...semen.filter(s => s.semen_batch_id), ...novas]);

  return (
    <div className="p-3 bg-white border border-slate-200 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Snowflake className="w-4 h-4 text-slate-900" />
          <h4 className="font-semibold text-slate-800 text-sm">Sêmen da sessão</h4>
        </div>
        {!disabled && (
          <button type="button" onClick={() => setEscolhendo(true)}
            className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded">
            <Plus className="w-3 h-3" /> Palheta
          </button>
        )}
      </div>

      {semen.length === 0 ? (
        <p className="text-xs text-slate-500">
          Nenhuma palheta lançada. O que for registrado aqui baixa do estoque;
          as gotas por doadora, na tabela abaixo, apenas rastreiam.
        </p>
      ) : (
        <div className="space-y-1.5">
          {semen.map((s, i) => {
            const b        = porId[s.semen_batch_id];
            const gravadas = dosesGravadas[s.semen_batch_id] || 0;
            const restante = b
              ? saldoProjetado(b.saldo_doses, gravadas, s.doses)
              : null;
            const faltou = restante !== null && restante < 0;

            return (
              <div key={`${s.semen_batch_id || 'nova'}-${i}`}
                className="flex items-center gap-2 text-xs">
                {s.semen_batch_id ? (
                  <span className="flex-1 min-w-0 truncate">
                    <span className="font-medium text-slate-800">{b?.touro || 'sem touro'}</span>
                    {b?.codigo && <span className="text-slate-600"> · {b.codigo}</span>}
                    {b?.dono && <span className="text-slate-500"> · {b.dono}</span>}
                    {b && enderecoDoBotijao(b) && (
                      <span className="text-slate-400"> ({enderecoDoBotijao(b)})</span>
                    )}
                  </span>
                ) : (
                  <button type="button" disabled={disabled} onClick={() => setEscolhendo(true)}
                    className="flex-1 min-w-0 text-left px-2 py-1 border border-dashed border-slate-300 rounded
                               text-blue-600 hover:bg-blue-50">
                    Escolher partida…
                  </button>
                )}

                <label className="flex items-center gap-1 shrink-0">
                  <span className="text-slate-500">Doses</span>
                  <input type="number" min="0.5" step="0.5"
                    className="w-16 px-1 py-1 border border-slate-200 rounded text-xs text-center
                               focus:outline-none focus:ring-1 focus:ring-blue-400"
                    value={s.doses ?? ''}
                    disabled={disabled}
                    onChange={(e) => alterar(i, 'doses', parseFloat(e.target.value) || 0)}
                  />
                </label>

                <span className={`shrink-0 w-28 text-right ${faltou ? 'font-semibold' : 'text-slate-500'}`}>
                  {restante === null ? '—'
                    : faltou ? `${MARK.WARN} faltam ${emDoses(Math.abs(restante))}`
                    : `restam ${emDoses(restante)}`}
                </span>

                {!disabled && (
                  <button type="button" onClick={() => remover(i)}
                    className="shrink-0 p-1 text-slate-400 hover:text-slate-700 rounded"
                    title="Tirar esta palheta da sessão">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* O saldo não trava a gravação: quem está na bancada sabe o que tirou do
          botijão melhor que o sistema, e recusar o lançamento faria a pessoa
          registrar errado para conseguir salvar. O aviso existe para revelar
          estoque desatualizado, não para impedir o trabalho. */}
      {semen.some(s => {
        const b = porId[s.semen_batch_id];
        return b && saldoProjetado(b.saldo_doses, dosesGravadas[s.semen_batch_id] || 0, s.doses) < 0;
      }) && (
        <p className="mt-2 text-xs text-slate-600">
          {MARK.WARN} Há mais doses lançadas do que o estoque registra. O lançamento
          é salvo assim mesmo — confira a entrada da partida no cadastro.
        </p>
      )}

      {escolhendo && (
        <SeletorDePalhetas
          batches={batches}
          fora={jaEscolhidas}
          dosesGravadas={dosesGravadas}
          clientId={clientId}
          clientName={clientName}
          onEscolher={adicionar}
          onFechar={() => setEscolhendo(false)}
        />
      )}
    </div>
  );
};

export default SemenDaSessao;
